import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Users, Plus, Settings, CheckCircle, Clock } from 'lucide-react';
import { equipesService } from '../services/api';
import { useNavigate } from 'react-router-dom';

const TeamSelector = ({ onTeamChange = () => {} }) => {
  const [equipes, setEquipes] = useState([]);
  const [equipeActive, setEquipeActive] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadEquipes();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const loadEquipes = async () => {
    try {
      setLoading(true);
      const response = await equipesService.getMesEquipes();
      
      if (response.success && response.data.length > 0) {
        setEquipes(response.data);
        
        // Sélectionner l'équipe principale ou la première équipe
        const equipePrincipale = response.data.find(e => e.est_equipe_principale);
        const activeTeam = equipePrincipale || response.data[0];
        
        setEquipeActive(activeTeam);
        onTeamChange(activeTeam);
      }
    } catch (err) {
      console.error('Erreur lors du chargement des équipes:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectEquipe = (equipe) => {
    setEquipeActive(equipe);
    setIsOpen(false);
    onTeamChange(equipe);
  };

  const handleCreateTeam = () => {
    setIsOpen(false);
    navigate('/personnes');
  };

  const handleManageTeam = () => {
    setIsOpen(false);
    navigate('/personnes');
  };

  if (loading) {
    return (
      <div className="flex items-center space-x-2 px-3 py-2 rounded-lg bg-gray-100 animate-pulse">
        <div className="w-4 h-4 bg-gray-300 rounded"></div>
        <div className="w-24 h-4 bg-gray-300 rounded"></div>
      </div>
    );
  }

  if (!equipeActive) {
    return (
      <button
        onClick={handleCreateTeam}
        className="flex items-center space-x-2 px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
      >
        <Plus className="w-4 h-4" />
        <span className="font-medium">Créer une équipe</span>
      </button>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 transition-colors min-w-48"
      >
        <div 
          className="w-4 h-4 rounded flex-shrink-0"
          style={{ backgroundColor: equipeActive.couleur }}
        />
        <span className="font-medium text-gray-900 truncate">
          {equipeActive.nom}
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
          {/* Header */}
          <div className="p-3 border-b border-gray-100">
            <h3 className="font-medium text-gray-900">Mes équipes</h3>
          </div>
          
          {/* Liste des équipes */}
          <div className="py-1">
            {equipes.map((equipe) => (
              <button
                key={equipe.id}
                onClick={() => handleSelectEquipe(equipe)}
                className={`w-full flex items-center space-x-3 px-3 py-3 hover:bg-gray-50 transition-colors ${
                  equipeActive.id === equipe.id ? 'bg-blue-50' : ''
                }`}
              >
                <div 
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                  style={{ backgroundColor: equipe.couleur }}
                >
                  {equipe.nom.charAt(0).toUpperCase()}
                </div>
                
                <div className="flex-1 text-left min-w-0">
                  <div className="flex items-center space-x-2">
                    <h4 className="font-medium text-gray-900 truncate">
                      {equipe.nom}
                    </h4>
                    {equipeActive.id === equipe.id && (
                      <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    )}
                  </div>
                  
                  <div className="flex items-center space-x-3 text-xs text-gray-500 mt-1">
                    <span className="flex items-center">
                      <Users className="w-3 h-3 mr-1" />
                      {equipe.statistiques?.nombre_membres || 0} membres
                    </span>
                    
                    {equipe.invitation_en_attente && (
                      <span className="flex items-center text-yellow-600">
                        <Clock className="w-3 h-3 mr-1" />
                        Invitation en attente
                      </span>
                    )}
                    
                    {equipe.est_equipe_principale && (
                      <span className="bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded text-xs font-medium">
                        Principale
                      </span>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
          
          {/* Actions */}
          <div className="border-t border-gray-100 py-1">
            <button
              onClick={handleCreateTeam}
              className="w-full flex items-center space-x-3 px-3 py-3 hover:bg-gray-50 transition-colors text-blue-600"
            >
              <div className="w-8 h-8 rounded-lg border-2 border-dashed border-blue-300 flex items-center justify-center">
                <Plus className="w-4 h-4" />
              </div>
              <span className="font-medium">Créer une nouvelle équipe</span>
            </button>
            
            <button
              onClick={handleManageTeam}
              className="w-full flex items-center space-x-3 px-3 py-3 hover:bg-gray-50 transition-colors text-gray-700"
            >
              <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                <Settings className="w-4 h-4" />
              </div>
              <span className="font-medium">Gérer personnes & équipes</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamSelector;
